import React from 'react';
import { cn } from '../../utils/cn';
import type { ScheduleViewMode } from '../../types/schedule';

/**
 * Scope toggle shared by SchedulePage (page header) and BasketScheduleView
 * (basket Live tab). Binary scope selector:
 *   Broker   (mode='default') — date-scoped, matches the broker's own
 *                               positions screen.
 *   Platform (mode='full')    — full cross-date scope, includes carry
 *                               from prior dates.
 */
export const ModeRadio: React.FC<{
  mode: ScheduleViewMode;
  onChange: (m: ScheduleViewMode) => void;
}> = ({ mode, onChange }) => {
  const options: { value: ScheduleViewMode; label: string; help: string }[] = [
    { value: 'default', label: 'Broker',   help: "Date-scoped — matches broker's own positions view" },
    { value: 'full',    label: 'Platform', help: 'Full cross-date scope — includes carry from prior dates' },
  ];
  return (
    <div
      className="inline-flex shrink-0 items-center bg-gray-100 dark:bg-gray-700/60 rounded-md p-0.5"
      role="radiogroup"
      aria-label="Schedule scope mode"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={mode === opt.value}
          title={opt.help}
          onClick={() => onChange(opt.value)}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap',
            mode === opt.value
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Wraps a toggle group with a tiny uppercase micro-label so users can tell
 * Scope and View apart at a glance — both are pill-style segmented controls
 * otherwise, which would read as one mega-toolbar.
 */
export const LabeledControl: React.FC<{ label: string; children: React.ReactNode }> = ({
  label, children,
}) => (
  <div className="flex shrink-0 items-center gap-1.5">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
      {label}
    </span>
    {children}
  </div>
);
